import { useParams } from 'react-router-dom';
import { HouseholdLayout } from '@/components/layouts/HouseholdLayout.jsx';
import { PageHeader } from '@/components/PageHeader';
import { ShoppingListItems } from '../components/ShoppingListItems';
import { AddShoppingListEntryForm } from '../components/AddShoppingListEntryForm';
import { useGetShoppingListEntriesQuery, usePatchShoppingListEntryMutation, useSetIsBoughtShoppingListEntriesMutation, useArchiveAllShoppingListEntriesMutation, useUnarchiveShoppingListEntriesMutation, useRestoreDeletedShoppingListEntriesMutation } from '../api';
import { Stack, Typography, Alert, Snackbar, Box, Checkbox } from '@mui/joy';
import { useState, useEffect } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import UndoIcon from '@mui/icons-material/Undo';
import { Button } from '@/ui/Button';
import { LinearProgressBar } from '@/ui/LinearProgressBar';

export function ShoppingListPage() {
  const { householdId } = useParams();
  const { data, isLoading } = useGetShoppingListEntriesQuery({ householdId });
  const [patchEntry] = usePatchShoppingListEntryMutation();
  const [undoState, setUndoState] = useState(null);

  const entries = data?.data || [];

  const handleToggleBought = async (entry) => {
    await patchEntry({
      householdId,
      id: entry.id,
      data: {
        is_bought: !entry.is_bought,
      }
    });
  };

  const hasEntries = entries.length > 0;
  const itemsBought = entries.filter(entry => entry.is_bought).length;
  const itemsToBuy = entries.filter(entry => !entry.is_bought).length;  

  return (
    <HouseholdLayout backgroundColor="gray">
      <Stack gap={2} direction="column">        
        <PageHeader title="Shopping" suffix={`${itemsToBuy} ${itemsToBuy > 1 ? 'items' : 'item'} to buy`} />
        <Stack gap={1.25} direction="column" sx={{ mt: -0.5 }}>
          <LinearProgressBar value={itemsBought} max={entries.length} />
          <SelectAllRow entries={entries} setUndoState={setUndoState} />
        </Stack>
        
        <AddShoppingListEntryForm />

        {isLoading ? (
          <Typography>Loading...</Typography>
        ) : !hasEntries ? (
          <Alert color="neutral" variant="soft">
            Your shopping list is empty.
          </Alert>
        ) : (<>          
          <ShoppingListItems items={entries} isEntryChecked={(entry) => entry.is_bought} crossOffSelected={true} onToggle={handleToggleBought} showRecipeSources={true} />
        </>)}
        <UndoState undoState={undoState} setUndoState={setUndoState} />
      </Stack>
    </HouseholdLayout>
  );
}

const hideUndoMessageAfterMs = 30000;

function SelectAllRow({ entries, setUndoState }) {
  const { householdId } = useParams();
  const [setIsBought] = useSetIsBoughtShoppingListEntriesMutation();
  const [archiveAll] = useArchiveAllShoppingListEntriesMutation();

  const boughtCount = entries.filter(entry => entry.is_bought).length;
  const hasBoughtItems = boughtCount > 0;
  const allBought = entries.length > 0 && boughtCount === entries.length;
  const hasItems = entries.length > 0;

  const handleArchiveBought = async () => {
    const { data: result } = await archiveAll({ householdId });
    if (result.data?.entry_ids?.length > 0) {
      setUndoState({
        type: 'archive',
        entryIds: result.data.entry_ids,
      });
    }
  };

  return (
    <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">      
      <Button color="neutral" shape="pill" size="sm" disabled={!hasItems} onClick={() => setIsBought({ householdId, isBought: !allBought })}>
        {hasBoughtItems && allBought ? 'Deselect all' : 'Select all'}
      </Button>
      <Button
        startDecorator={<CheckIcon />}        
        color={hasBoughtItems ? 'primary' : 'neutral'}
        disabled={!hasBoughtItems}
        shape="pill"
        size="sm"
        onClick={handleArchiveBought}             
      >
        Finish {boughtCount > 0 ? ` · ${boughtCount}` : ''}
      </Button>
    </Stack>
  );
}

function UndoState({ undoState, setUndoState }) {
  const { householdId } = useParams();
  const [restoreDeleted] = useRestoreDeletedShoppingListEntriesMutation();
  const [unarchiveEntries] = useUnarchiveShoppingListEntriesMutation();

  useEffect(() => {
    if (undoState) {
      const timer = setTimeout(() => {
        setUndoState(null);
      }, hideUndoMessageAfterMs);

      return () => clearTimeout(timer);
    }
  }, [undoState]);

  const handleUndo = async () => {
    if (!undoState) return;

    try {
      if (undoState.type === 'delete') {
        await restoreDeleted({ householdId, entryIds: undoState.entryIds });
      } else if (undoState.type === 'archive') {
        await unarchiveEntries({ householdId, entryIds: undoState.entryIds });
      }
      setUndoState(null);
    } catch (error) {
      console.error('Failed to undo action:', error);
    }
  };

  if (!undoState) return null;

  const message = undoState.type === 'delete' ? 'Deleted ' : 'Archived ';
  const itemCount = undoState.entryIds.length;
  const itemCountText = itemCount !== 1 ? 'items' : 'item';

  return (
    <Snackbar
      color={undoState.type === 'delete' ? 'neutral' : 'primary'}
      variant="solid"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      open={!!undoState}
      invertedColors
      onClose={() => setUndoState(null)}
      endDecorator={
        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          startDecorator={<UndoIcon />}
          onClick={handleUndo}
        >
          Undo
        </Button>
      }
    >
      {message} {itemCount} {itemCountText}
    </Snackbar>
  );
}
